import React, { useState, useEffect } from 'react';
import { fetchWithAuth, REGISTRATIONS_API } from '../../utils/api';

const RegistrationDataMonitor = ({ registrationId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [routines, setRoutines] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchLatestData = async () => {
    if (!registrationId) return;
    
    setIsLoading(true);
    try {
      console.log('🔍 Fetching latest registration data...');
      
      // Fetch main registration
      const regData = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}`);
      console.log('📋 Registration data:', regData);
      setRegistrationData(regData);

      // Fetch participants
      const participantsData = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}/participants`);
      console.log('👥 Participants data:', participantsData);
      setParticipants(participantsData);

      // Fetch routines
      const routinesData = await fetchWithAuth(`${REGISTRATIONS_API}/${registrationId}/routines`);
      console.log('💃 Routines data:', routinesData);
      setRoutines(routinesData);

      setLastUpdated(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('❌ Error fetching registration data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (registrationId && isVisible) {
      fetchLatestData();
    }
  }, [registrationId, isVisible]);

  if (!registrationId) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {!isVisible ? (
        <button
          onClick={() => setIsVisible(true)}
          className="bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-colors"
          title="Open Registration Data Monitor"
        >
          🔍
        </button>
      ) : (
        <div className="bg-white border border-gray-300 rounded-lg shadow-xl w-96 max-h-96 overflow-hidden">
          {/* Header */}
          <div className="bg-purple-600 text-white px-4 py-2 flex justify-between items-center">
            <h3 className="font-medium text-sm">Registration Monitor</h3>
            <div className="flex gap-2">
              <button
                onClick={fetchLatestData}
                disabled={isLoading}
                className="bg-purple-500 hover:bg-purple-400 px-2 py-1 rounded text-xs"
                title="Refresh Data"
              >
                {isLoading ? '🔄' : '🔄'}
              </button>
              <button
                onClick={() => setIsVisible(false)}
                className="bg-purple-500 hover:bg-purple-400 px-2 py-1 rounded text-xs"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-80">
            <div className="text-xs text-gray-500 mb-3">
              ID: {registrationId}
              {lastUpdated && (
                <div>Last updated: {lastUpdated}</div>
              )}
            </div>

            {/* Registration Status */}
            {registrationData && (
              <div className="mb-3">
                <h4 className="font-medium text-sm mb-1">📋 Registration</h4>
                <div className="bg-gray-100 p-2 rounded text-xs">
                  <div>Status: <span className="font-mono">{registrationData.status}</span></div>
                  <div>Current Step: <span className="font-mono">{registrationData.currentStep || 0}</span></div>
                  <div>Step Path: <span className="font-mono">{registrationData.currentStepPath || 'overview'}</span></div>
                  <div>Last Completed: <span className="font-mono">{registrationData.lastCompletedStep || 'none'}</span></div>
                </div>
              </div>
            )}

            {/* Participants */}
            {participants && (
              <div className="mb-3">
                <h4 className="font-medium text-sm mb-1">👥 Participants</h4>
                <div className="bg-gray-100 p-2 rounded text-xs">
                  <div>Dancers: <span className="font-mono">{participants.dancers?.length || 0}</span></div>
                  <div>Teachers: <span className="font-mono">{participants.teachers?.length || 0}</span></div>
                  <div>Observers: <span className="font-mono">{participants.observers?.length || 0}</span></div>
                  
                  {participants.dancers && participants.dancers.length > 0 && (
                    <div className="mt-2">
                      <div className="font-medium">Dancers:</div>
                      {participants.dancers.slice(0, 3).map((dancer, idx) => (
                        <div key={idx} className="ml-2">
                          • {dancer.firstName} {dancer.lastName}
                          {dancer.workshopLevel && (
                            <span className="text-blue-600"> ({dancer.workshopLevel})</span>
                          )}
                        </div>
                      ))}
                      {participants.dancers.length > 3 && (
                        <div className="ml-2 text-gray-500">
                          ... and {participants.dancers.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Routines */}
            {routines && (
              <div className="mb-3">
                <h4 className="font-medium text-sm mb-1">💃 Routines</h4>
                <div className="bg-gray-100 p-2 rounded text-xs">
                  <div>Count: <span className="font-mono">{routines.routines?.length || 0}</span></div>
                  
                  {routines.routines && routines.routines.length > 0 && (
                    <div className="mt-2">
                      {routines.routines.slice(0, 3).map((routine, idx) => (
                        <div key={idx} className="ml-2">
                          • {routine.title || routine.name || 'Untitled'}
                          {routine.style && (
                            <span className="text-blue-600"> ({routine.style})</span>
                          )}
                        </div>
                      ))}
                      {routines.routines.length > 3 && (
                        <div className="ml-2 text-gray-500">
                          ... and {routines.routines.length - 3} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Raw Data Toggle */}
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-gray-600">Raw Data</summary>
              <div className="mt-2 bg-gray-50 p-2 rounded overflow-auto max-h-32">
                <pre className="text-xs">
                  {JSON.stringify({ registrationData, participants, routines }, null, 2)}
                </pre>
              </div>
            </details>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationDataMonitor;































